import React, { useState, useEffect, useRef } from 'react';
import { Check } from 'lucide-react';
import { formatInTimeZone } from 'date-fns-tz';
import { FiUser, FiMapPin, FiClock, FiBriefcase, FiAlertCircle } from 'react-icons/fi';
import { useQueryClient } from '@tanstack/react-query';

import { useSchedulerStatus } from '../contexts/SchedulerStatusContext';
import { usePublishAccess } from '../contexts/PublishAccessContext';
import { auth } from '../firebase';
import {
  getSchedulerApiUrl,
  getSchedulerAuthToken,
  schedulerApiFetch,
} from '../api/schedulerClient';
import { useUserConfig, useInvalidateUserConfig } from '../queries/useUserConfig';
import { userConfigKeys } from '../queries/assignmentChunkKeys';
import SchedulerCardStatusSection from '../features/scheduler/SchedulerCardStatusSection';
import UnifiedSchedulerDrawer from '../features/scheduler/UnifiedSchedulerDrawer';
import {
  displayedServerEnvironment,
  inferServerEnvironmentFromNemtBaseUrl,
} from '../utils/nemtEnvironment';
import { primeSchedulerNotificationSound } from '../utils/schedulerNotificationSound';
import { useTimeFilter } from '../contexts/TimeFilterContext';
import {
  ASSIGNMENT_CHUNK_TYPES,
  loadSessionChunksBatch,
} from '../services/firebaseService';

const schedulerApiUrl = getSchedulerApiUrl();

const getAuthToken = getSchedulerAuthToken;

function isValidIanaTimeZone(tz) {
  if (!tz || typeof tz !== 'string') return false;
  try {
    Intl.DateTimeFormat('en-US', { timeZone: tz }).format(new Date());
    return true;
  } catch {
    return false;
  }
}

/** Firebase value when untouched; form value while editing (including empty). */
function getConfigFieldDisplay(field, configFormData, currentConfig, configDirtyFields) {
  if (configDirtyFields[field]) {
    const edited = configFormData[field];
    return edited == null ? '' : String(edited);
  }
  const stored = currentConfig?.[field];
  return stored == null || stored === undefined ? '' : String(stored);
}

function buildDetailApiUrlFromNemtRoot(nemtRoot) {
  const root = String(nemtRoot || '').trim().replace(/\/$/, '');
  return root ? `${root}/users/me` : '';
}


const SchedulerCard = ({ embedded = false, headerTitleExtra = null, headerActionsExtra = null, statsExtra = null, onSyncComplete = null }) => {
  const {
    schedulerStatus,
    fetchSchedulerStatus,
    statusPollError,
    lastSchedulerApiError,
    markSchedulerPollingActive,
  } = useSchedulerStatus();
  const { selectedResultId } = useTimeFilter();
  const queryClient = useQueryClient();
  const { refetch: refetchUserConfig } = useUserConfig();
  const invalidateUserConfig = useInvalidateUserConfig();
  const { adminModeUnlocked } = usePublishAccess();
  const [currentConfig, setCurrentConfig] = useState(null);
  const [showUnifiedDrawer, setShowUnifiedDrawer] = useState(false);
  const [configFormData, setConfigFormData] = useState({});
  const [configDirtyFields, setConfigDirtyFields] = useState({});
  const [isStarting, setIsStarting] = useState(false);
  const [isStopping, setIsStopping] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [connectionError, setConnectionError] = useState('');
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [modalConfig, setModalConfig] = useState(null);
  const [modalConfigSource, setModalConfigSource] = useState('live'); // 'session' | 'live'
  const [isLoadingModalConfig, setIsLoadingModalConfig] = useState(false);
  const [activeConfigTab, setActiveConfigTab] = useState('planner');
  const [customPlanningDate, setCustomPlanningDate] = useState(null);
  const [customPlanningEndDate, setCustomPlanningEndDate] = useState(null);
  const [useCustomPlanningTimes, setUseCustomPlanningTimes] = useState(false);
  const [use2HourWindow, setUse2HourWindow] = useState(false);
  const [leglockNotification, setLeglockNotification] = useState('');
  const [companyCurrentTime, setCompanyCurrentTime] = useState(new Date());
  const [companyDetails, setCompanyDetails] = useState(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isSavingApiSettings, setIsSavingApiSettings] = useState(false);
  const [syncMessage, setSyncMessage] = useState({ type: '', text: '' });
  const [syncTrigger, setSyncTrigger] = useState(0);
  const [dateValidationError, setDateValidationError] = useState('');
  const prevSchedulerRunningRef = useRef(undefined);
  const portalHandoffSyncStartedRef = useRef(false);

  // Validate date range only when custom window is enabled and both bounds are set
  useEffect(() => {
    if (use2HourWindow) {
      setDateValidationError('');
      return;
    }
    if (!useCustomPlanningTimes || !customPlanningDate || !customPlanningEndDate) {
      setDateValidationError('');
      return;
    }

    const effectiveStartTime = customPlanningDate.getTime();
    const effectiveEndTime = customPlanningEndDate.getTime();
    if (effectiveStartTime >= effectiveEndTime) {
      setDateValidationError('End time must be after the start time.');
    } else {
      setDateValidationError('');
    }
  }, [customPlanningDate, customPlanningEndDate, use2HourWindow, useCustomPlanningTimes]);

  useEffect(() => {
    if (use2HourWindow) setUseCustomPlanningTimes(false);
  }, [use2HourWindow]);

  useEffect(() => {
    if (!adminModeUnlocked && use2HourWindow) {
      setUse2HourWindow(false);
      setCustomPlanningEndDate(null);
    }
  }, [adminModeUnlocked, use2HourWindow]);

  useEffect(() => {
    if (!schedulerStatus) {
      prevSchedulerRunningRef.current = undefined;
      return;
    }
    const wasRunning = prevSchedulerRunningRef.current;
    const nowRunning = schedulerStatus.is_running;
    if (wasRunning === true && nowRunning === false) {
      setStatusMessage((msg) => {
        if (typeof msg === 'string' && msg.includes('Stop request sent')) {
          return 'Scheduler stopped successfully!';
        }
        return msg;
      });
    }
    prevSchedulerRunningRef.current = nowRunning;
  }, [schedulerStatus]);

  const fetchCurrentConfig = async ({ forceRefresh = false } = {}) => {
    try {
      const authToken = await getAuthToken();

      // Try to fetch user-specific config from Firebase first
      let userConfig = null;
      let isFirebaseConfig = false;

      if (authToken) {
        try {
          const uid = auth.currentUser?.uid;
          if (forceRefresh) {
            const userConfigResponse = await schedulerApiFetch('user-config', {
              cacheBust: true,
              bearerToken: authToken,
            });
            if (userConfigResponse.ok) {
              const userConfigData = await userConfigResponse.json();
              if (userConfigData.success && userConfigData.config) {
                userConfig = userConfigData.config;
                isFirebaseConfig = !userConfigData.is_default;
                if (uid) {
                  queryClient.setQueryData(userConfigKeys.detail(uid), {
                    config: userConfig,
                    isDefault: Boolean(userConfigData.is_default),
                  });
                }
                console.log('Loaded user config from Firebase (forced refresh):', isFirebaseConfig ? 'user-specific' : 'defaults');
              }
            }
          } else {
            const cached = uid
              ? queryClient.getQueryData(userConfigKeys.detail(uid))
              : null;
            if (cached?.config) {
              userConfig = cached.config;
              isFirebaseConfig = !cached.isDefault;
              console.log('Loaded user config from React Query cache:', isFirebaseConfig ? 'user-specific' : 'defaults');
            } else {
              const refetched = await refetchUserConfig();
              if (refetched.data?.config) {
                userConfig = refetched.data.config;
                isFirebaseConfig = !refetched.data.isDefault;
                console.log('Loaded user config from shared query:', isFirebaseConfig ? 'user-specific' : 'defaults');
              }
            }
          }
        } catch (e) {
          console.warn('Could not load user config from shared query, falling back to server config:', e);
        }
      }

      // Fetch scheduler config (this is not user-specific)
      const schedulerResponse = await schedulerApiFetch('scheduler-config', {
        cacheBust: true,
      });

      let schedulerConfig = {};
      if (schedulerResponse.ok) {
        schedulerConfig = await schedulerResponse.json();
      }

      // If we got user config from Firebase, use it; otherwise use clean defaults.
      // Do not fall back to /config here: that endpoint reflects mutable backend
      // module state and can contain another user's recently applied settings.
      let plannerConfig = userConfig;
      if (!plannerConfig) {
        const plannerResponse = await schedulerApiFetch('default-config', {
          cacheBust: true,
        });

        if (plannerResponse.ok) {
          const defaultConfigData = await plannerResponse.json();
          plannerConfig = defaultConfigData.success ? defaultConfigData.config : null;
          if (!plannerConfig) {
            setConnectionError('Default scheduler configuration was empty');
            return;
          }
        } else {
          setConnectionError(`API returned status ${plannerResponse.status} when fetching config`);
          return;
        }
      }

      // Combine both configs for the form
      const combinedConfig = {
        ...plannerConfig,
        ...schedulerConfig,
        _isFirebaseConfig: isFirebaseConfig  // Track if this came from Firebase
      };

      setCurrentConfig(combinedConfig);
      setConfigFormData(combinedConfig);
      setConfigDirtyFields({});
      setConnectionError(''); // Clear any previous errors
      return combinedConfig;
    } catch (error) {
      console.error('Error fetching config:', error);
      setConnectionError(`Cannot connect to scheduler API at ${schedulerApiUrl}. Make sure the FastAPI server is running on this machine.`);
      return null;
    }
  };

  /**
   * Load config for View Details drawer.
   * Prefer session metadata.planner_config when a historical result is selected,
   * so we show the params that run was executed with.
   * Fallback: GET /user-config (live Firebase user config).
   */
  const fetchModalConfig = async () => {
    setIsLoadingModalConfig(true);
    setModalConfigSource('live');
    try {
      const authToken = await getAuthToken();

      // 0) Prefer planner_config snapshot from the selected historical session
      let sessionPlannerConfig = null;
      if (selectedResultId && !String(selectedResultId).startsWith('mock-')) {
        try {
          const { mainDoc, chunks } = await loadSessionChunksBatch(selectedResultId, [
            ASSIGNMENT_CHUNK_TYPES.METADATA,
          ]);
          const metadata =
            chunks?.[ASSIGNMENT_CHUNK_TYPES.METADATA] ||
            mainDoc?.results?.metadata ||
            null;
          if (metadata?.planner_config && typeof metadata.planner_config === 'object') {
            sessionPlannerConfig = metadata.planner_config;
            console.log('View Details: using session planner_config snapshot');
          } else if (metadata?.constraints_enabled?.away_from_home_enabled != null) {
            // Older sessions may only have constraints_enabled AFH fields
            sessionPlannerConfig = {
              away_from_home_enabled: !!metadata.constraints_enabled.away_from_home_enabled,
              away_from_home_radius_miles:
                metadata.constraints_enabled.away_from_home_radius_miles ?? 10.0,
              away_from_home_shift_threshold_pct:
                metadata.constraints_enabled.away_from_home_shift_threshold_pct ?? 0.0,
            };
            console.log('View Details: using session constraints_enabled AFH fields');
          }
        } catch (e) {
          console.warn('View Details: session metadata load failed, falling back to live config', e);
        }
      }

      // 1) Prefer Firebase user-config (source of truth — same path as fetchCurrentConfig)
      let plannerConfig = null;
      if (authToken) {
        try {
          const userConfigResponse = await schedulerApiFetch('user-config', {
            cacheBust: true,
            bearerToken: authToken,
          });
          if (userConfigResponse.ok) {
            const userConfigData = await userConfigResponse.json();
            if (userConfigData.success && userConfigData.config) {
              plannerConfig = userConfigData.config;
              console.log(
                'View Details: using user-config (Firebase)',
                userConfigData.is_default ? '(defaults merged)' : '(user-specific)'
              );
            }
          }
        } catch (e) {
          console.warn('View Details: user-config fetch failed, will fall back to /config', e);
        }
      }

      // 2) Scheduler interval — not reliably stored in user doc; keep separate
      let schedulerConfig = {};
      try {
        const schedulerResponse = await schedulerApiFetch('scheduler-config', {
          cacheBust: true,
        });
        if (schedulerResponse.ok) {
          schedulerConfig = await schedulerResponse.json();
        }
      } catch (e) {
        console.warn('View Details: scheduler-config fetch failed', e);
      }

      // 3) Fallback: clean defaults only. Avoid /config because it is mutable
      // backend module state and may reflect another user's applied config.
      if (!plannerConfig) {
        const plannerResponse = await schedulerApiFetch('default-config', {
          cacheBust: true,
        });
        if (!plannerResponse.ok) {
          console.error('Failed to fetch modal config (no user-config and default-config failed)');
          setModalConfig(null);
          return;
        }
        const defaultConfigData = await plannerResponse.json();
        plannerConfig = defaultConfigData.success ? defaultConfigData.config : null;
        if (!plannerConfig) {
          console.error('Failed to fetch modal config: default config was empty');
          setModalConfig(null);
          return;
        }
        console.log('View Details: using default-config fallback');
      }

      // 4) Optional space-types for load_unload_times (same as before)
      let spaceTypesData = {};
      try {
        const spaceTypesResponse = await schedulerApiFetch('space-types', {
          cacheBust: true,
          bearerToken: authToken || undefined,
        });
        if (spaceTypesResponse.ok) {
          spaceTypesData = await spaceTypesResponse.json();
        }
      } catch (_) {
        /* optional */
      }

      // Session snapshot overlays live config when available (historical run params win)
      const effectivePlannerConfig = sessionPlannerConfig
        ? { ...plannerConfig, ...sessionPlannerConfig }
        : plannerConfig;

      const combinedConfig = {
        ...effectivePlannerConfig,
        ...schedulerConfig,
        // interval_minutes: scheduler wins; if missing, keep from planner or default
        interval_minutes:
          schedulerConfig.interval_minutes ??
          effectivePlannerConfig.interval_minutes ??
          currentConfig?.interval_minutes ??
          30,
        load_unload_times:
          spaceTypesData.success && spaceTypesData.load_unload_times
            ? spaceTypesData.load_unload_times
            : effectivePlannerConfig.load_unload_times || {}
      };

      setModalConfig(combinedConfig);
      setModalConfigSource(sessionPlannerConfig ? 'session' : 'live');
    } catch (error) {
      console.error('Error fetching modal config:', error);
      setModalConfig(null);
      setModalConfigSource('live');
    } finally {
      setIsLoadingModalConfig(false);
    }
  };

  // Update backend config without starting scheduler
  // NEW: This now saves to Firebase for user-specific configuration
  const updateBackendConfig = async (configUpdates) => {
    try {
      console.log('SchedulerCard: Sending config updates to Firebase:', configUpdates);
      const authToken = await getAuthToken();
      if (!authToken) {
        console.warn('No auth token available for config update');
        return { success: false, error: 'No auth token' };
      }

      const requestBody = {
        config: configUpdates,
        scheduler_config: {
          interval_minutes: 30 // dummy value, not used for config updates
        }
      };

      console.log('SchedulerCard: Making request to:', `${schedulerApiUrl}/update-config`);
      console.log('SchedulerCard: Request body:', JSON.stringify(requestBody, null, 2));
      const response = await schedulerApiFetch('update-config', {
        method: 'POST',
        bearerToken: authToken,
        body: requestBody,
      });
      console.log('SchedulerCard: Update config response status:', response.status);
      const responseText = await response.text();
      console.log('SchedulerCard: Update config response:', responseText);

      if (!response.ok) {
        console.error('SchedulerCard: Update config failed:', responseText);
        return { success: false, error: responseText };
      } else {
        const result = JSON.parse(responseText);
        console.log('SchedulerCard: Config saved to Firebase:', result.saved_to_firebase);
        return { success: true, saved_to_firebase: result.saved_to_firebase };
      }
    } catch (error) {
      console.warn('Error updating backend config:', error);
      return { success: false, error: error.message };
    }
  };

  const saveApiSettings = async () => {
    const nemtApiBase = getConfigFieldDisplay(
      'nemt_api_base_url',
      configFormData,
      currentConfig,
      configDirtyFields
    ).trim();
    if (!nemtApiBase) {
      setSyncMessage({
        type: 'error',
        text: 'NEMT API base URL is required. Enter your tenant API root (e.g. https://host/api).',
      });
      return;
    }

    const jwtToken = getConfigFieldDisplay(
      'api_jwt_token',
      configFormData,
      currentConfig,
      configDirtyFields
    ).trim();
    setIsSavingApiSettings(true);
    setSyncMessage({ type: '', text: '' });
    try {
      const updates = {
        nemt_api_base_url: nemtApiBase,
      };
      if (jwtToken) {
        updates.api_jwt_token = jwtToken;
      }
      // Persist dirty planner flags (e.g. mapper_shifts_enabled) when saving API settings.
      Object.keys(configDirtyFields).forEach((field) => {
        if (
          ![
            'interval_minutes',
            'trips_api_url',
            'drivers_api_url',
            'publishing_api_url',
            'nemt_api_base_url',
            'api_jwt_token',
          ].includes(field)
        ) {
          updates[field] = configFormData[field];
        }
      });

      const result = await updateBackendConfig(updates);
      if (!result.success) {
        let detail = result.error || 'Failed to save API settings';
        try {
          const parsed = JSON.parse(detail);
          if (parsed.detail) detail = typeof parsed.detail === 'string' ? parsed.detail : JSON.stringify(parsed.detail);
        } catch {
          /* use raw */
        }
        throw new Error(detail);
      }

      if (!result.saved_to_firebase) {
        throw new Error('API settings could not be confirmed in Firebase.');
      }

      invalidateUserConfig();
      await fetchCurrentConfig({ forceRefresh: true });
      setConfigDirtyFields((prev) => {
        const next = { ...prev };
        delete next.nemt_api_base_url;
        delete next.api_jwt_token;
        return next;
      });

      if (jwtToken) {
        // Sync after Firebase save — force backend to use values just saved (not stale module/Firebase read).
        const details = await fetchCompanyDetails(false, {
          notifyParent: false,
          configApply: updates,
          jwtOverride: jwtToken,
        });
        if (details) {
          setSyncMessage({
            type: 'success',
            text: 'API settings saved and company details synced.',
          });
        } else {
          setSyncMessage({
            type: 'error',
            text: 'API settings saved, but company sync failed. Check JWT and base URL, then use Sync Data.',
          });
        }
      } else {
        setSyncMessage({
          type: 'success',
          text: 'API settings saved. Enter a JWT token and use Sync Data to refresh company details.',
        });
      }
    } catch (e) {
      setSyncMessage({ type: 'error', text: e.message || 'Failed to save API settings' });
    } finally {
      setIsSavingApiSettings(false);
    }
  };

  // Fetch company details from Detail API
  const fetchCompanyDetails = async (showMessage = true, opts = {}) => {
    const notifyParent = opts.notifyParent !== false;
    const configApply = opts.configApply;
    console.log('SchedulerCard: fetchCompanyDetails called with showMessage:', showMessage, 'notifyParent:', notifyParent);
    // Check if JWT token exists
    const jwtToken = String(
      opts.jwtOverride ??
      getConfigFieldDisplay('api_jwt_token', configFormData, currentConfig, configDirtyFields) ??
      ''
    ).trim();

    if (!jwtToken) {
      if (showMessage) {
        setSyncMessage({ type: 'error', text: 'Please enter JWT Token first before syncing company details.' });
      }
      return null;
    }

    setIsSyncing(true);
    if (showMessage) {
      setSyncMessage({ type: '', text: '' });
    }

    try {
      // Ensure the backend is using the user's latest env/token BEFORE fetching detail_api_url.
      // This prevents "LIVE selected but /config still returns DEV detail_api_url" race conditions.
      const authToken = await getAuthToken();
      if (!authToken) {
        throw new Error('Authentication required. Please sign in again.');
      }

      if (configApply) {
        await updateBackendConfig(configApply);
      } else {
        const nemtApiBase = getConfigFieldDisplay(
          'nemt_api_base_url',
          configFormData,
          currentConfig,
          configDirtyFields
        ).trim();
        const preflight = { api_jwt_token: jwtToken };
        if (nemtApiBase) {
          preflight.nemt_api_base_url = nemtApiBase;
        }
        await updateBackendConfig(preflight);
      }

      const nemtRootForMe =
        (configApply && String(configApply.nemt_api_base_url || '').trim()) ||
        getConfigFieldDisplay(
          'nemt_api_base_url',
          configFormData,
          currentConfig,
          configDirtyFields
        ).trim();

      let detailApiUrl = buildDetailApiUrlFromNemtRoot(nemtRootForMe);

      // Avoid GET /config returning a stale detail_api_url right after save (Firebase read lag).
      if (!detailApiUrl) {
        const configResponse = await schedulerApiFetch('config', {
          cacheBust: true,
          bearerToken: authToken,
        });

        if (!configResponse.ok) {
          throw new Error('Failed to fetch config from backend');
        }

        const config = await configResponse.json();
        detailApiUrl = config.detail_api_url;
      }

      if (!detailApiUrl) {
        throw new Error('Detail API URL not configured. Set NEMT API base URL first.');
      }

      // eslint-disable-next-line no-console
      console.log('[Company sync] GET /users/me:', detailApiUrl);

      // Hit the Detail API with JWT token
      const response = await fetch(detailApiUrl, {
        method: 'GET',
        headers: {
          'Authorization': jwtToken,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        let errBody = '';
        try {
          errBody = (await response.text()).slice(0, 240);
        } catch { /* ignore */ }
        // eslint-disable-next-line no-console
        console.error('[Company sync] /users/me failed:', {
          url: detailApiUrl,
          status: response.status,
          bodyPreview: errBody,
        });
        throw new Error(`API returned status ${response.status}`);
      }

      let data;
      try {
        data = await response.json();
      } catch (parseErr) {
        // eslint-disable-next-line no-console
        console.error('[Company sync] /users/me response is not valid JSON:', {
          url: detailApiUrl,
          error: parseErr?.message || parseErr,
        });
        throw new Error('Detail API response is not valid JSON');
      }

      if (data.success && data.user) {
        const companyIdFromApi = data.user._id != null && String(data.user._id).trim() !== ''
          ? String(data.user._id).trim()
          : null;
        const details = {
          focalPersonName: data.user.focalPerson?.displayName || 'N/A',
          companyName: data.user.displayName || 'N/A',
          address: data.user.address || 'N/A',
          timeZone: data.user.timeZone || null,
          state: data.user.state || 'N/A',
          companyId: companyIdFromApi,
        };

        setCompanyDetails(details);

        // Collect all config updates
        const configUpdates = {};

        // Always include JWT token so backend has the latest token for API calls
        configUpdates.api_jwt_token = jwtToken;

        const nemtForSync = (
          (configApply && String(configApply.nemt_api_base_url || '').trim()) ||
          getConfigFieldDisplay(
            'nemt_api_base_url',
            configFormData,
            currentConfig,
            configDirtyFields
          ).trim()
        );
        if (nemtForSync) {
          configUpdates.nemt_api_base_url = nemtForSync;
        }

        // Auto-fill timezone if available
        if (details.timeZone) {
          handleConfigChange('la_timezone', details.timeZone);
          configUpdates.la_timezone = details.timeZone;
        }

        // Auto-fill company ID and name if available
        if (companyIdFromApi) {
          const companyName = data.user.displayName || 'Unknown Company';
          console.log('SchedulerCard: Extracted company_id:', companyIdFromApi, 'company_name:', companyName);
          handleConfigChange('company_id', companyIdFromApi);
          handleConfigChange('company_name', companyName);
          configUpdates.company_id = companyIdFromApi;
          configUpdates.company_name = companyName;
        } else {
          console.log('SchedulerCard: No company_id found in response, data.user:', data.user);
        }

        // Update backend config immediately with all updates
        if (Object.keys(configUpdates).length > 0) {
          console.log('SchedulerCard: Updating backend config with:', configUpdates);
          await updateBackendConfig(configUpdates);
        }

        if (nemtForSync) {
          setCurrentConfig((prev) => (prev ? { ...prev, nemt_api_base_url: nemtForSync } : prev));
          setConfigFormData((prev) => ({ ...prev, nemt_api_base_url: nemtForSync }));
        }

        if (showMessage) {
          setSyncMessage({ type: 'success', text: 'Company details synced successfully!' });
        }

        // Trigger parent refresh if provided (TripsPage uses this to reload results).
        // IMPORTANT: For the Unified Drawer "silent auto-sync on open", we do NOT want to
        // reload latest Firebase sessions because it can unmount the page section and
        // close the drawer unexpectedly.
        if (notifyParent && onSyncComplete) {
          onSyncComplete();
        }

        // Increment sync trigger to signal child selection components to refresh
        setSyncTrigger(prev => prev + 1);

        return details;
      } else {
        // eslint-disable-next-line no-console
        console.error('[Company sync] /users/me invalid payload:', {
          url: detailApiUrl,
          success: data?.success,
          hasUser: Boolean(data?.user),
        });
        throw new Error('Invalid response format from API');
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[Company sync] /users/me error:', error?.message || error, error);
      if (showMessage) {
        setSyncMessage({ type: 'error', text: `Failed to sync: ${error.message}` });
      }
      return null;
    } finally {
      setIsSyncing(false);
    }
  };

  const runCompanySyncFromConfig = async (cfg, opts = {}) => {
    if (!cfg?.api_jwt_token?.trim()) return null;
    const nemt = String(cfg.nemt_api_base_url || '').trim();
    const jwt = cfg.api_jwt_token.trim();
    const syncOpts = {
      notifyParent: opts.notifyParent !== false,
      jwtOverride: jwt,
    };
    if (nemt) {
      syncOpts.configApply = {
        api_jwt_token: jwt,
        nemt_api_base_url: nemt,
      };
      if (opts.portalEnvironment) {
        syncOpts.configApply.server_environment = opts.portalEnvironment;
      }
    }
    return fetchCompanyDetails(false, syncOpts);
  };

  const handleCloseUnifiedDrawer = () => {
    setShowUnifiedDrawer(false);
    setConfigDirtyFields({});
    if (currentConfig) {
      setConfigFormData(currentConfig);
    }
  };

  // Step 1: Show the Unified Drawer — always reload from Firebase so toggles match saved user config.
  const handleShowUnifiedDrawer = async () => {
    setStatusMessage('');
    const cfg = await fetchCurrentConfig({ forceRefresh: true });
    setShowUnifiedDrawer(true);
    if (cfg?.api_jwt_token) {
      runCompanySyncFromConfig(cfg, { notifyParent: false });
    }
  };

  const handleShowConfigForm = handleShowUnifiedDrawer;
  const handleStartScheduler = handleShowUnifiedDrawer;

  // Step 2: When drivers are selected, actually start the scheduler
  const handleStartPlanningConfirm = async (selectedDriverIds, frozenDriverIds = [], freezePreAssignDriverIds = [], selectedTripIds = null, perDriverMaxLoad = {}, preservePortalBuckets = false, useCapPlan = false, useFleetMapping = false) => {
    primeSchedulerNotificationSound();
    setShowUnifiedDrawer(false);

    // Check if user is authenticated first
    const currentUser = auth.currentUser;
    if (!currentUser) {
      setStatusMessage('Please sign in first to start the scheduler.');
      console.error('SchedulerCard: User not authenticated');
      return;
    }

    console.log('SchedulerCard: User authenticated:', currentUser.email);
    setIsStarting(true);
    setStatusMessage('');

    try {
      // Send only fields changed in this drawer session. The backend loads the
      // user's Firebase config first, so sending the full form can write fallback
      // defaults into Firebase when a refresh/cache miss loaded defaults.
      const plannerConfig = Object.keys(configDirtyFields).reduce((acc, field) => {
        if (!['interval_minutes', 'trips_api_url', 'drivers_api_url', 'publishing_api_url'].includes(field)) {
          acc[field] = configFormData[field];
        }
        return acc;
      }, {});
      console.log('SchedulerCard: configFormData contains company_id:', configFormData.company_id);
      console.log('SchedulerCard: plannerConfig will contain company_id:', plannerConfig.company_id);

      // null = backend uses default (now → end of day in company TZ). 2hr window sends end only.
      const sendCustom =
        !use2HourWindow && useCustomPlanningTimes && customPlanningDate && customPlanningEndDate;
      if (use2HourWindow) {
        plannerConfig.custom_planning_datetime = null;
        let endDatetimeToSend = customPlanningEndDate;
        if (customPlanningEndDate && companyTimezone) {
          const hm = formatInTimeZone(customPlanningEndDate, companyTimezone, 'HH:mm');
          if (hm === '23:59') {
            const sec = parseInt(formatInTimeZone(customPlanningEndDate, companyTimezone, 'ss'), 10);
            const ms = parseInt(formatInTimeZone(customPlanningEndDate, companyTimezone, 'SSS'), 10);
            const addMs = (59 - sec) * 1000 + (999 - ms);
            endDatetimeToSend = new Date(customPlanningEndDate.getTime() + addMs);
          }
        }
        plannerConfig.custom_planning_end_datetime = endDatetimeToSend ? endDatetimeToSend.toISOString() : null;
      } else if (sendCustom) {
        plannerConfig.custom_planning_datetime = customPlanningDate.toISOString();
        let endDatetimeToSend = customPlanningEndDate;
        if (companyTimezone) {
          const hm = formatInTimeZone(customPlanningEndDate, companyTimezone, 'HH:mm');
          if (hm === '23:59') {
            const sec = parseInt(formatInTimeZone(customPlanningEndDate, companyTimezone, 'ss'), 10);
            const ms = parseInt(formatInTimeZone(customPlanningEndDate, companyTimezone, 'SSS'), 10);
            const addMs = (59 - sec) * 1000 + (999 - ms);
            endDatetimeToSend = new Date(customPlanningEndDate.getTime() + addMs);
          }
        }
        plannerConfig.custom_planning_end_datetime = endDatetimeToSend.toISOString();
      } else {
        plannerConfig.custom_planning_datetime = null;
        plannerConfig.custom_planning_end_datetime = null;
      }

      const intervalParsed = Number(configFormData.interval_minutes ?? currentConfig?.interval_minutes ?? 30);
      const intervalMinutes =
        Number.isFinite(intervalParsed) && intervalParsed > 0 ? intervalParsed : 30;

      const schedulerConfigPayload = adminModeUnlocked
        ? { interval_minutes: intervalMinutes, continuous_loop: true }
        : { continuous_loop: false };

      const requestBody = {
        config: plannerConfig,
        scheduler_config: schedulerConfigPayload,
        selected_driver_ids: selectedDriverIds, // Pass selected driver IDs
        // null = legacy: full trip pool each run; array = strict session selection
        selected_trip_ids: Array.isArray(selectedTripIds) ? selectedTripIds : null,
        frozen_driver_ids: Array.isArray(frozenDriverIds) ? frozenDriverIds : [], // Pass frozen driver IDs for bucket retention
        freeze_pre_assign_driver_ids: Array.isArray(freezePreAssignDriverIds) ? freezePreAssignDriverIds : [], // Pass freeze pre-assign (API-assigned bucket, add more only if no lateness)
        per_driver_max_load: perDriverMaxLoad && typeof perDriverMaxLoad === 'object' && Object.keys(perDriverMaxLoad).length > 0 ? perDriverMaxLoad : null,
        preserve_portal_buckets: !!preservePortalBuckets, // Session-only: lock non-selected portal buckets (Preserved)
        useCapPlan: !!useCapPlan,
        useFleetMapping: !!useFleetMapping,
      };

      // Get Firebase auth token
      console.log('SchedulerCard: Getting auth token...');
      const authToken = await getAuthToken();
      console.log('SchedulerCard: Auth token obtained:', authToken ? 'YES' : 'NO');

      if (!authToken) {
        console.error('SchedulerCard: No auth token available');
        setStatusMessage('Authentication required. Please sign in again.');
        setIsStarting(false);
        return;
      }

      console.log('SchedulerCard: Making request to:', `${schedulerApiUrl}/start`);
      const response = await schedulerApiFetch('start', {
        method: 'POST',
        bearerToken: authToken,
        body: requestBody,
      });

      console.log('SchedulerCard: Response status:', response.status);

      const result = await response.json();

      if (response.ok && result.success) {
        setShowUnifiedDrawer(false);
        markSchedulerPollingActive();
        fetchSchedulerStatus(); // Refresh status immediately
        invalidateUserConfig();
        fetchCurrentConfig({ forceRefresh: true }); // Refresh config to show updated values
      } else {
        setStatusMessage(`Failed to start scheduler: ${result.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error starting scheduler:', error);
      setStatusMessage(`Error starting scheduler: ${error.message}`);
    } finally {
      setIsStarting(false);
    }
  };

  const handleStopScheduler = async () => {
    primeSchedulerNotificationSound();
    console.log('Sending immediate stop request to scheduler...');

    // Check if user is authenticated first
    const currentUser = auth.currentUser;
    if (!currentUser) {
      setStatusMessage('Please sign in first to stop the scheduler.');
      console.error('SchedulerCard: User not authenticated');
      return;
    }

    setIsStopping(true);
    setStatusMessage('');

    try {
      // Get Firebase auth token
      const authToken = await getAuthToken();
      if (!authToken) {
        setStatusMessage('Authentication required. Please sign in again.');
        setIsStopping(false);
        return;
      }

      const response = await schedulerApiFetch('stop', {
        method: 'POST',
        bearerToken: authToken,
      });

      const result = await response.json();
      console.log('Stop response:', result);

      if (response.ok && result.success) {
        console.log('Immediate stop request successful, refreshing status...');
        // Refresh status to confirm it's stopped
        fetchSchedulerStatus();
        setTimeout(() => fetchSchedulerStatus(), 1000);
      } else {
        setStatusMessage(`Failed to stop scheduler: ${result.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error stopping scheduler:', error);
      setStatusMessage(`Error stopping scheduler: ${error.message}`);
    } finally {
      setIsStopping(false);
    }
  };


  const handleConfigChange = (field, value) => {
    setConfigDirtyFields((prevDirty) => {
      const nextDirty = { ...prevDirty, [field]: true };
      setConfigFormData((prev) => {
        const next = { ...prev, [field]: value };
        // Manual base URL edit: preview inferred env unless user toggled dev/live.
        if (field === 'nemt_api_base_url') {
          next.server_environment = inferServerEnvironmentFromNemtBaseUrl(value);
        }
        return next;
      });
      return nextDirty;
    });
  };

  const formatElapsedTime = (elapsedTime) => {
    if (!elapsedTime) return '00:00:00';
    return elapsedTime;
  };

  useEffect(() => {
    const unsub = auth.onAuthStateChanged((firebaseUser) => {
      if (firebaseUser) {
        fetchCurrentConfig();
      }
    });
    return unsub;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // After /portal_oauth redirect: sync company once using routed base_url (sessionStorage handoff).
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (firebaseUser) => {
      if (!firebaseUser || portalHandoffSyncStartedRef.current) return;

      let handoff;
      try {
        const raw = sessionStorage.getItem('portal_oauth_handoff');
        if (!raw) return;
        handoff = JSON.parse(raw);
        sessionStorage.removeItem('portal_oauth_handoff');
      } catch {
        return;
      }

      const nemt = String(handoff?.nemt_api_base_url || '').trim();
      const jwt = String(handoff?.api_jwt_token || '').trim();
      if (!nemt || !jwt) return;

      portalHandoffSyncStartedRef.current = true;

      const cfg = await fetchCurrentConfig();
      const syncCfg = {
        ...(cfg || {}),
        api_jwt_token: jwt,
        nemt_api_base_url: nemt,
      };
      await runCompanySyncFromConfig(syncCfg, {
        notifyParent: false,
        portalEnvironment: handoff.server_environment,
      });

      setCurrentConfig((prev) => ({
        ...(prev || {}),
        api_jwt_token: jwt,
        nemt_api_base_url: nemt,
        server_environment:
          handoff.server_environment ||
          inferServerEnvironmentFromNemtBaseUrl(nemt) ||
          prev?.server_environment,
      }));
      setConfigFormData((prev) => ({
        ...prev,
        api_jwt_token: jwt,
        nemt_api_base_url: nemt,
        server_environment:
          handoff.server_environment ||
          inferServerEnvironmentFromNemtBaseUrl(nemt) ||
          prev?.server_environment,
      }));
      await fetchCurrentConfig();
    });

    return unsubscribe;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Calculate 2-hour window end time
  // Simply adds 2 hours to current UTC time - the toISOString() will send correct UTC
  // and server will interpret it correctly in company timezone
  const get2HourWindowEndTime = () => {
    const now = new Date();
    return new Date(now.getTime() + (2 * 60 * 60 * 1000));
  };

  // Company timezone from Firebase config (used for date picker and display)
  const companyTimezone = React.useMemo(() => {
    const candidate = currentConfig?.la_timezone || companyDetails?.timeZone || 'America/Los_Angeles';
    return isValidIanaTimeZone(candidate) ? candidate : 'America/Los_Angeles';
  }, [currentConfig?.la_timezone, companyDetails?.timeZone]);

  // Format time for company timezone display
  const formatCompanyTime = (date) => {
    return date.toLocaleString('en-US', {
      timeZone: companyTimezone,
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short'
    });
  };

  // Live clock update for company timezone
  useEffect(() => {
    const clockInterval = setInterval(() => {
      setCompanyCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(clockInterval);
  }, []);

  // Keep end date updated when 2-hour window is active
  useEffect(() => {
    if (use2HourWindow) {
      // Update end date every minute when 2-hour window is active
      const updateEndDate = () => {
        setCustomPlanningEndDate(get2HourWindowEndTime());
      };

      updateEndDate(); // Initial update
      const intervalId = setInterval(updateEndDate, 60000); // Update every minute

      return () => clearInterval(intervalId);
    }
  }, [use2HourWindow]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <SchedulerCardStatusSection
        schedulerApiUrl={schedulerApiUrl}
        connectionError={connectionError || statusPollError}
        setConnectionError={setConnectionError}
        fetchSchedulerStatus={fetchSchedulerStatus}
        fetchCurrentConfig={fetchCurrentConfig}
        schedulerStatus={schedulerStatus}
        apiError={lastSchedulerApiError}
        handleShowConfigForm={handleShowConfigForm}
        isStarting={isStarting}
        isStopping={isStopping}
        handleStopScheduler={handleStopScheduler}
        formatElapsedTime={formatElapsedTime}
        statusMessage={statusMessage}
        fetchModalConfig={fetchModalConfig}
        setModalConfig={setModalConfig}
        setShowDetailsModal={setShowDetailsModal}
        embedded={embedded}
        headerTitleExtra={headerTitleExtra}
        headerActionsExtra={headerActionsExtra}
        statsExtra={statsExtra}
        showViewDetails={
          Boolean(schedulerStatus?.is_running) ||
          Boolean(selectedResultId && !String(selectedResultId).startsWith('mock-'))
        }
      />
      <UnifiedSchedulerDrawer
        isOpen={showUnifiedDrawer}
        onClose={handleCloseUnifiedDrawer}
        onStart={handleStartPlanningConfirm}
        schedulerApiUrl={schedulerApiUrl}
        getAuthToken={getAuthToken}
        
        activeConfigTab={activeConfigTab}
        setActiveConfigTab={setActiveConfigTab}
        currentConfig={currentConfig}
        configFormData={configFormData}
        handleConfigChange={handleConfigChange}
        leglockNotification={leglockNotification}
        setLeglockNotification={setLeglockNotification}
        fetchCompanyDetails={fetchCompanyDetails}
        isSyncing={isSyncing}
        isSavingApiSettings={isSavingApiSettings}
        saveApiSettings={saveApiSettings}
        configDirtyFields={configDirtyFields}
        getConfigFieldDisplay={getConfigFieldDisplay}
        syncMessage={syncMessage}
        syncTrigger={syncTrigger}
        companyDetails={companyDetails}
        customPlanningDate={customPlanningDate}
        setCustomPlanningDate={setCustomPlanningDate}
        customPlanningEndDate={customPlanningEndDate}
        setCustomPlanningEndDate={setCustomPlanningEndDate}
        useCustomPlanningTimes={useCustomPlanningTimes}
        setUseCustomPlanningTimes={setUseCustomPlanningTimes}
        use2HourWindow={use2HourWindow}
        setUse2HourWindow={setUse2HourWindow}
        dateValidationError={dateValidationError}
        companyTimezone={companyTimezone}
        companyCurrentTime={companyCurrentTime}
        formatCompanyTime={formatCompanyTime}
        get2HourWindowEndTime={get2HourWindowEndTime}
        isStarting={isStarting}
      />

      {/* Configuration Details Panel */}
      <div className={`fixed top-0 right-0 h-full w-full max-w-xl bg-white border-l border-main-base shadow-xl transform transition-transform duration-300 ease-in-out z-[2100] flex flex-col ${showDetailsModal ? 'translate-x-0' : 'translate-x-full'}`}>

        {/* Panel Header — matches DriverCard header style */}
        <div className="bg-main-primary text-white px-3 py-2.5 flex items-center justify-between flex-shrink-0 shadow-lg">
          <div className="flex items-center gap-2 min-w-0">
            <svg className="w-3.5 h-3.5 opacity-80 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 14.25h13.5m-13.5 0a3 3 0 01-3-3m3 3a3 3 0 100 6h13.5a3 3 0 100-6m-16.5-3a3 3 0 013-3h13.5a3 3 0 013 3m-19.5 0a4.5 4.5 0 01.9-2.7L5.737 5.1a3.375 3.375 0 012.7-1.35h7.126c1.062 0 2.062.5 2.7 1.35l2.587 3.45a4.5 4.5 0 01.9 2.7m0 0a3 3 0 01-3 3m0 3h.008v.008h-.008v-.008zm0-6h.008v.008h-.008v-.008zm-3 6h.008v.008h-.008v-.008zm0-6h.008v.008h-.008v-.008z" />
            </svg>
            <h2 className="text-xs font-semibold uppercase tracking-tight leading-none">Scheduler Configuration</h2>
            {schedulerStatus?.is_running && (
              <span className="flex items-center gap-1 ml-1 px-1.5 py-0.5 rounded bg-white/10 border border-white/15">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse flex-shrink-0" />
                <span className="text-[9px] font-black uppercase tracking-widest text-emerald-300">Live</span>
              </span>
            )}
          </div>
          <button
            onClick={() => setShowDetailsModal(false)}
            className="p-1 rounded hover:bg-white/10 transition-colors flex-shrink-0"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Scrollable Body */}
        <div className="flex-1 overflow-y-auto">
          {isLoadingModalConfig ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="animate-spin rounded-full h-7 w-7 border-2 border-main-base border-t-main-primary mb-3"></div>
              <p className="text-[11px] text-main-light font-medium">Loading configuration...</p>
            </div>
          ) : !modalConfig ? (
            <div className="flex flex-col items-center justify-center py-16">
              <FiAlertCircle className="text-main-danger w-6 h-6 mb-2" />
              <p className="text-[11px] text-main-light font-medium">Failed to load configuration</p>
            </div>
          ) : (
            <div className="divide-y divide-main-base">
              {modalConfigSource === 'session' && (
                <div className="px-3 py-2 bg-main-primary/5 border-b border-main-base">
                  <div className="text-[10px] font-semibold text-main-primary">
                    Showing settings from this session run
                  </div>
                  <div className="text-[9px] text-main-light mt-0.5">
                    These were the params used when this result was generated
                  </div>
                </div>
              )}

              {/* ── Planning Window ── */}
              <div className="px-3 py-2.5">
                <div className="text-[9px] font-black uppercase tracking-[0.12em] text-main-primary opacity-60 mb-2">Planning Window</div>
                <div className="rounded-md border border-main-base overflow-hidden divide-y divide-main-base">
                  <div className="flex items-center justify-between px-2.5 py-1.5 bg-white">
                    <span className="text-[10px] text-main-light">Start Time</span>
                    <span className="text-[10px] font-bold text-main-primary tabular-nums">
                      {(() => {
                        const start = customPlanningDate || new Date();
                        return start.toLocaleString('en-US', { timeZone: modalConfig.la_timezone || 'America/Los_Angeles', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
                      })()}
                    </span>
                  </div>
                  <div className="flex items-center justify-between px-2.5 py-1.5 bg-main-base-50">
                    <span className="text-[10px] text-main-light">End Time</span>
                    <span className="text-[10px] font-bold text-main-primary tabular-nums">
                      {(() => {
                        if (customPlanningEndDate) {
                          return customPlanningEndDate.toLocaleString('en-US', { timeZone: modalConfig.la_timezone || 'America/Los_Angeles', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
                        }
                        const start = customPlanningDate || new Date();
                        const dateStr = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: modalConfig.la_timezone || 'America/Los_Angeles' });
                        return `${dateStr}, 11:59 PM`;
                      })()}
                    </span>
                  </div>
                  <div className="flex items-center justify-between px-2.5 py-1.5 bg-white">
                    <span className="text-[10px] text-main-light">Timezone</span>
                    <span className="text-[10px] font-bold font-mono text-main-primary">{modalConfig.la_timezone}</span>
                  </div>
                  {adminModeUnlocked && (
                  <div className="flex items-center justify-between px-2.5 py-1.5 bg-main-base-50">
                    <span className="text-[10px] text-main-light">Run Interval</span>
                    <span className="text-[10px] font-bold text-main-primary tabular-nums">{modalConfig.interval_minutes} minutes</span>
                  </div>
                  )}
                </div>
              </div>

              {/* ── Driver Constraints ── */}
              <div className="px-3 py-2.5">
                <div className="text-[9px] font-black uppercase tracking-[0.12em] text-main-primary opacity-60 mb-2">Driver Constraints</div>
                <div className="rounded-md border border-main-base overflow-hidden divide-y divide-main-base">
                  {[
                    { label: 'Max Distance per Driver', value: `${modalConfig.max_distance_per_driver} miles`, color: 'text-main-primary' },
                    { label: 'Max Trips per Driver', value: modalConfig.max_trips_per_driver, color: 'text-main-primary' },
                    { label: 'Max Deadhead Miles', value: `${modalConfig.max_deadhead_miles} miles`, color: 'text-yellow-600' },
                    { label: 'Max Overtime Minutes', value: `${modalConfig.max_overtime_minutes} min`, color: 'text-main-primary' },
                    { label: 'Max Trip Distance', value: `${modalConfig.max_trip_distance_miles} miles`, color: 'text-main-primary' },
                    { label: 'Overtime Allowed', value: modalConfig.allow_overtime ? 'Yes' : 'No', color: modalConfig.allow_overtime ? 'text-emerald-600' : 'text-main-light' },
                  ].map(({ label, value, color }, idx) => (
                    <div key={label} className={`flex items-center justify-between px-2.5 py-1.5 ${idx % 2 === 0 ? 'bg-white' : 'bg-main-base-50'}`}>
                      <span className="text-[10px] text-main-light">{label}</span>
                      <span className={`text-[10px] font-bold tabular-nums ${color}`}>{value}</span>
                    </div>
                  ))}
                </div>
                {/* Exceed flags */}
                <div className="rounded-md border border-main-base overflow-hidden divide-y divide-main-base mt-1.5">
                  {[
                    { label: 'Exceed Max Distance', val: modalConfig.allow_exceed_max_distance },
                    { label: 'Exceed Max Deadhead', val: modalConfig.allow_exceed_max_deadhead },
                    { label: 'Exceed Max Trip Distance', val: modalConfig.allow_exceed_max_trip_distance },
                  ].map(({ label, val }, idx) => (
                    <div key={label} className={`flex items-center justify-between px-2.5 py-1.5 ${idx % 2 === 0 ? 'bg-white' : 'bg-main-base-50'}`}>
                      <span className="text-[10px] text-main-light">{label}</span>
                      <span className={`text-[10px] font-bold ${val ? 'text-main-primary' : 'text-main-light'}`}>{val ? 'Yes' : 'No'}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* ── Load / Unload Times ── */}
              <div className="px-3 py-2.5">
                <div className="text-[9px] font-black uppercase tracking-[0.12em] text-main-primary opacity-60 mb-2">Load / Unload Times</div>
                {modalConfig.load_unload_times && Object.keys(modalConfig.load_unload_times).length > 0 ? (
                  (() => {
                    const entries = Object.entries(modalConfig.load_unload_times);
                    const seenTitles = new Set();
                    const hasPrimarySource = entries.some(([, data]) => data.source === 'st_primary');
                    const filteredEntries = entries.filter(([, data]) => {
                      if (data.status && data.status !== 'active') return false;
                      if (hasPrimarySource && data.source === 'los_fallback') return false;
                      const displayKey = `${data.los_title || data.title}-${data.st_title || data.title}-${data.category}`;
                      if (seenTitles.has(displayKey)) return false;
                      seenTitles.add(displayKey);
                      return true;
                    });

                    if (filteredEntries.length === 0) {
                      return <div className="py-3 text-center text-[10px] text-main-light border border-main-base rounded-md bg-main-base-50">No active space types found.</div>;
                    }

                    const categoryMeta = {
                      wheelchair: { label: 'Wheelchair' },
                      ambulatory: { label: 'Ambulatory' },
                      stretcher:  { label: 'Stretcher' },
                    };

                    // Group by category
                    const grouped = filteredEntries.reduce((acc, [id, data]) => {
                      const cat = data.category || 'other';
                      if (!acc[cat]) acc[cat] = [];
                      acc[cat].push([id, data]);
                      return acc;
                    }, {});

                    const categoryOrder = ['wheelchair', 'ambulatory', 'stretcher'];
                    const allCats = [...categoryOrder.filter(c => grouped[c]), ...Object.keys(grouped).filter(c => !categoryOrder.includes(c))];

                    return (
                      <div className="rounded-md border border-main-base overflow-hidden">
                        {/* Table column header */}
                        <div className="grid grid-cols-12 gap-x-2 px-2.5 py-1.5 bg-main-base-100 border-b border-main-base">
                          <div className="col-span-6 text-[9px] font-black uppercase tracking-tight text-main-primary">Space Type</div>
                          <div className="col-span-2 text-center text-[9px] font-black uppercase tracking-tight text-main-primary">Load</div>
                          <div className="col-span-2 text-center text-[9px] font-black uppercase tracking-tight text-main-primary">Unload</div>
                          <div className="col-span-2 text-right text-[9px] font-black uppercase tracking-tight text-main-primary">Unit</div>
                        </div>

                        {allCats.map((cat) => {
                          const meta = categoryMeta[cat] || { label: cat.charAt(0).toUpperCase() + cat.slice(1) };
                          return (
                            <div key={cat}>
                              {/* Category group header */}
                              <div className="flex items-center justify-between px-2.5 py-1 bg-main-base-100 border-b border-t border-main-base">
                                <span className="text-[9px] font-black uppercase tracking-widest text-main-primary">{meta.label}</span>
                                <span className="text-[9px] font-bold text-main-light">{grouped[cat].length} type{grouped[cat].length !== 1 ? 's' : ''}</span>
                              </div>
                              {/* Rows */}
                              {grouped[cat].map(([spaceTypeId, spaceTypeData], rowIdx) => {
                                const lineLeft = spaceTypeData.los_title || spaceTypeData.title || '—';
                                const lineRight = spaceTypeData.st_title || spaceTypeData.original_title || spaceTypeData.title || '—';
                                const displayTitle = lineLeft === lineRight ? lineLeft : `${lineLeft} (${lineRight})`;
                                return (
                                  <div key={spaceTypeId} className={`grid grid-cols-12 gap-x-2 px-2.5 py-1.5 items-center border-b border-main-base last:border-b-0 ${rowIdx % 2 === 0 ? 'bg-white' : 'bg-main-base-50'}`}>
                                    <div className="col-span-6 min-w-0">
                                      <div className="text-[10px] font-semibold text-main-primary leading-snug break-words" title={displayTitle}>{displayTitle}</div>
                                    </div>
                                    <div className="col-span-2 text-center text-[10px] font-bold tabular-nums text-main-primary">
                                      {spaceTypeData.load ?? '—'}
                                    </div>
                                    <div className="col-span-2 text-center text-[10px] font-bold tabular-nums text-main-primary">
                                      {spaceTypeData.unload ?? '—'}
                                    </div>
                                    <div className="col-span-2 text-right text-[9px] text-main-light">min</div>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()
                ) : (
                  <div className="py-3 text-center text-[10px] text-main-light border border-main-base rounded-md bg-main-base-50">No space types loaded yet.</div>
                )}
              </div>

              {/* ── Optimization Settings ── */}
              <div className="px-3 py-2.5">
                <div className="text-[9px] font-black uppercase tracking-[0.12em] text-main-primary opacity-60 mb-2">Optimization Settings</div>
                <div className="rounded-md border border-main-base overflow-hidden divide-y divide-main-base">
                  {[
                    { label: 'Combined Planner', desc: 'Runs LegLock, group multiload, single multiload, then insertion', val: modalConfig.combined_planner_enabled },
                    { label: 'LegLock Planner', desc: 'Keeps all multi-leg trips assigned to the same driver', val: modalConfig.leglock_enabled },
                    { label: 'Multiload Planner', desc: 'Assigns multiple passengers to the same vehicle', val: modalConfig.multiload_enabled, extra: modalConfig.multiload_enabled && modalConfig.multiload_max_distance_miles ? `Max distance: ${modalConfig.multiload_max_distance_miles} miles` : null },
                    { label: 'Away From Home', desc: 'Dropoff must stay within garage radius', val: modalConfig.away_from_home_enabled, extra: modalConfig.away_from_home_enabled && modalConfig.away_from_home_radius_miles != null ? `Max distance: ${modalConfig.away_from_home_radius_miles} miles` : null },
                    { label: 'Local Optimization', desc: 'Swaps trips between drivers to improve assignments', val: modalConfig.local_optimization_enabled },
                    { label: 'Late Trip Detection', desc: 'Detects onway trips where driver ETA exceeds appointment time', val: modalConfig.late_trip_detection_enabled },
                    { label: 'Trip Assumption', desc: 'Predicts multi-leg B times using historical will-call data', val: modalConfig.trip_assumption_enabled },
                    { label: 'Break Trip Processing', desc: 'Skips drivers during break windows and shows breaks in timelines', val: modalConfig.enable_break_trips },
                    { label: 'Mapper', desc: 'Uses planner mapper APIs for shifts and fleet assignments', val: modalConfig.mapper_shifts_enabled },
                    { label: 'GIS', desc: 'Uses GIS Service API for routing-based distance and ETA', val: modalConfig.use_gis_api },
                    { label: 'Force Assignment', desc: 'Overrides hard constraints during planning', val: modalConfig.force_assignment },
                  ].map(({ label, desc, val, extra }, idx) => (
                    <div key={label} className={`flex items-start justify-between gap-3 px-2.5 py-2 ${idx % 2 === 0 ? 'bg-white' : 'bg-main-base-50'}`}>
                      <div className="min-w-0 flex-1">
                        <div className="text-[10px] font-semibold text-main-primary leading-tight">{label}</div>
                        <div className="text-[9px] text-main-light leading-snug mt-0.5">{desc}</div>
                        {extra && <div className="text-[9px] font-semibold text-main-primary mt-0.5">{extra}</div>}
                      </div>
                      <span className={`flex-shrink-0 text-[10px] font-bold mt-0.5 ${val ? 'text-main-primary' : 'text-main-light'}`}>
                        {val ? 'On' : 'Off'}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Force assignment overrides */}
                {modalConfig.force_assignment && (
                  <div className="mt-1.5 rounded-md border border-main-base overflow-hidden divide-y divide-main-base">
                    {[
                      { key: 'force_ignore_service_area', label: 'Ignore Service Area' },
                      { key: 'force_ignore_fleet_compatibility', label: 'Ignore Fleet Compatibility' },
                      { key: 'force_ignore_funding_source', label: 'Ignore Funding Source' },
                      { key: 'force_ignore_shift_time', label: 'Ignore Shift Time' },
                      { key: 'force_preferred_driver', label: 'Force Preferred Driver' },
                    ].map(({ key, label }, idx) => (
                      <div key={key} className={`flex items-center justify-between px-2.5 py-1.5 ${idx % 2 === 0 ? 'bg-white' : 'bg-main-base-50'}`}>
                        <span className="text-[10px] text-main-light">{label}</span>
                        <span className={`text-[10px] font-bold ${modalConfig[key] ? 'text-main-primary' : 'text-main-light'}`}>{modalConfig[key] ? 'Yes' : 'No'}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* ── Violation Sets ── */}
              <div className="px-3 py-2.5">
                <div className="text-[9px] font-black uppercase tracking-[0.12em] text-main-primary opacity-60 mb-2">Violation Sets</div>
                {[
                  {
                    title: 'Leg A',
                    rows: [
                      ['Pickup Early', modalConfig.cautions_pickup_early_min],
                      ['Pickup Late', modalConfig.cautions_pickup_late_min],
                      ['Dropoff Late', modalConfig.cautions_dropoff_late_min],
                      ['Dropoff Early', modalConfig.cautions_dropoff_early_min],
                    ],
                  },
                  {
                    title: 'Other legs',
                    rows: [
                      ['Pickup Early', modalConfig.cautions_other_pickup_early_min],
                      ['Pickup Late', modalConfig.cautions_other_pickup_late_min],
                      ['Dropoff Late', modalConfig.cautions_other_dropoff_late_min],
                      ['Dropoff Early', modalConfig.cautions_other_dropoff_early_min],
                    ],
                  },
                ].map((group) => (
                  <div key={group.title} className="mb-2">
                    <div className="text-[9px] font-bold uppercase tracking-wide text-main-light mb-1">{group.title}</div>
                    <div className="rounded-md border border-main-base overflow-hidden divide-y divide-main-base">
                      {group.rows.map(([label, raw], idx) => (
                        <div key={label} className={`flex items-center justify-between px-2.5 py-1.5 ${idx % 2 === 0 ? 'bg-white' : 'bg-main-base-50'}`}>
                          <span className="text-[10px] text-main-light">{label}</span>
                          <span className="text-[10px] font-bold tabular-nums text-main-primary">{`${raw ?? 0} min`}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
 
              {/* ── Company Details ── */}
              <div className="px-3 py-2.5">
                <div className="text-[9px] font-black uppercase tracking-[0.12em] text-main-primary opacity-60 mb-2">Company Details</div>

                {adminModeUnlocked && !modalConfig.api_jwt_token && (
                  <div className="mb-2 flex items-start gap-2 px-2.5 py-2 bg-red-50 border border-red-200 rounded-md">
                    <FiAlertCircle className="text-red-500 w-3 h-3 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-[10px] font-semibold text-red-700">JWT Token missing — API authentication will fail</p>
                    </div>
                  </div>
                )}

                <div className="rounded-md border border-main-base overflow-hidden divide-y divide-main-base">
                  {(companyDetails?.companyName || modalConfig.company_name) && (
                    <div className="flex items-center gap-2 px-2.5 py-2 bg-white">
                      <FiBriefcase className="w-3 h-3 text-main-light flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="text-[9px] font-black uppercase tracking-tight text-main-light">Company</div>
                        <div className="text-[10px] font-semibold text-main-primary truncate">{companyDetails?.companyName || modalConfig.company_name}</div>
                      </div>
                    </div>
                  )}
                  {companyDetails?.focalPersonName && (
                    <div className="flex items-center gap-2 px-2.5 py-2 bg-main-base-50">
                      <FiUser className="w-3 h-3 text-main-light flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="text-[9px] font-black uppercase tracking-tight text-main-light">Focal Person</div>
                        <div className="text-[10px] font-semibold text-main-primary truncate">{companyDetails.focalPersonName}</div>
                      </div>
                    </div>
                  )}
                  <div className="flex items-center gap-2 px-2.5 py-2 bg-white">
                    <FiClock className="w-3 h-3 text-main-light flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-[9px] font-black uppercase tracking-tight text-main-light">Timezone</div>
                      <div className="text-[10px] font-bold font-mono text-main-primary">{companyDetails?.timeZone || modalConfig.la_timezone || 'America/Los_Angeles'}</div>
                    </div>
                  </div>
                  {companyDetails?.address && (
                    <div className="flex items-start gap-2 px-2.5 py-2 bg-main-base-50">
                      <FiMapPin className="w-3 h-3 text-main-light flex-shrink-0 mt-0.5" />
                      <div className="min-w-0 flex-1">
                        <div className="text-[9px] font-black uppercase tracking-tight text-main-light">Address</div>
                        <div className="text-[10px] font-semibold text-main-primary">{companyDetails.address}{companyDetails.state && `, ${companyDetails.state}`}</div>
                      </div>
                    </div>
                  )}
                  {adminModeUnlocked && (
                    <>
                      <div className="flex items-center gap-2 px-2.5 py-2 bg-white">
                        <div className="w-3 h-3 flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="text-[9px] font-black uppercase tracking-tight text-main-light">Environment</div>
                          <div className="mt-0.5">
                            {displayedServerEnvironment(
                              modalConfig.nemt_api_base_url,
                              modalConfig.server_environment
                            ) === 'live' ? (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border bg-emerald-50 border-emerald-200 text-[9px] font-black uppercase tracking-tight text-emerald-700">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" />Live
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border bg-blue-50 border-blue-200 text-[9px] font-black uppercase tracking-tight text-blue-700">
                                <span className="w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0" />Dev
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 px-2.5 py-2 bg-main-base-50">
                        <div className="w-3 h-3 flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="text-[9px] font-black uppercase tracking-tight text-main-light">NEMT API base</div>
                          <div className="text-[10px] font-mono text-main-secondary mt-0.5 break-all">{modalConfig.nemt_api_base_url || <span className="text-red-500 font-semibold">Not configured</span>}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 px-2.5 py-2 bg-white">
                        <div className="w-3 h-3 flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="text-[9px] font-black uppercase tracking-tight text-main-light">JWT Token</div>
                          <div className="text-[10px] font-mono text-main-secondary mt-0.5">{modalConfig.api_jwt_token ? '••••••••••••••••••••••••••••' : <span className="text-red-500 font-semibold">Not configured</span>}</div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>

            </div>
          )}
        </div>

        {/* Panel Footer */}
        {modalConfig && (
          <div className="flex-shrink-0 px-3 py-2 border-t border-main-base bg-main-base-50">
            <button onClick={() => setShowDetailsModal(false)} className="btn-primary !py-1.5 !px-4 !text-xs w-full">
              Close
            </button>
          </div>
        )}
      </div>

      {/* Backdrop */}
      {showDetailsModal && (
        <div
          className="fixed inset-0 bg-black/40 z-[2099]"
          onClick={() => setShowDetailsModal(false)}
        />
      )}

    </>
  );
};

export default SchedulerCard;
